/**
 * components/UI.jsx
 * Shared primitive components used throughout the app.
 */

// ─── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 20, color = "#1F3864" }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size,
      border: `2px solid ${color}22`, borderTopColor: color,
      borderRadius: "50%", animation: "spin 0.7s linear infinite",
      flexShrink: 0,
    }} />
  );
}

// ─── Error banner ─────────────────────────────────────────────────────────────
export function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10,
      padding: "12px 16px", color: "#991B1B", fontSize: 13, fontWeight: 500,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 16,
    }}>
      <span>⚠ {message}</span>
      {onDismiss && <button onClick={onDismiss} style={{ background:"none", border:"none", cursor:"pointer", color:"#991B1B", fontSize:18, lineHeight:1 }}>×</button>}
    </div>
  );
}

// ─── Success banner ───────────────────────────────────────────────────────────
export function SuccessBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10,
      padding: "12px 16px", color: "#166534", fontSize: 13, fontWeight: 500,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 16,
    }}>
      <span>✓ {message}</span>
      {onDismiss && <button onClick={onDismiss} style={{ background:"none", border:"none", cursor:"pointer", color:"#166534", fontSize:18, lineHeight:1 }}>×</button>}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, style = {} }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
      padding: "20px 24px", ...style,
    }}>
      {children}
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
export function Skeleton({ height = 20, width = "100%", style = {} }) {
  return (
    <div style={{
      height, width, borderRadius: 6,
      background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      ...style,
    }} />
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ children, color = "gray" }) {
  const colors = {
    red:   "#FEF2F2:#DC2626", green: "#F0FDF4:#16A34A",
    blue:  "#EFF6FF:#2563EB", amber: "#FFFBEB:#D97706",
    gray:  "#F9FAFB:#4B5563", purple:"#F5F3FF:#7C3AED",
  };
  const [bg, fg] = (colors[color] || colors.gray).split(":");
  return (
    <span style={{
      background: bg, color: fg, padding: "2px 9px", borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
export function Empty({ icon = "◎", title, sub, action }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px", color: "#9CA3AF" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{title}</div>
      {sub && <div style={{ fontSize: 13, marginBottom: 20 }}>{sub}</div>}
      {action}
    </div>
  );
}

// ─── Button ───────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, variant = "primary", size = "md", loading: isLoading, disabled, style = {} }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 8, border: "none",
    borderRadius: 8, cursor: disabled || isLoading ? "not-allowed" : "pointer",
    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, transition: "all 0.15s",
    opacity: disabled || isLoading ? 0.6 : 1,
  };
  const variants = {
    primary: { background: "#1F3864", color: "#fff", padding: size === "sm" ? "6px 14px" : "9px 20px", fontSize: size === "sm" ? 12 : 14 },
    secondary: { background: "#F3F4F6", color: "#374151", padding: size === "sm" ? "6px 14px" : "9px 20px", fontSize: size === "sm" ? 12 : 14, border: "1px solid #E5E7EB" },
    danger: { background: "#FEF2F2", color: "#DC2626", padding: size === "sm" ? "6px 14px" : "9px 20px", fontSize: size === "sm" ? 12 : 14, border: "1px solid #FECACA" },
    ghost: { background: "transparent", color: "#6B7280", padding: size === "sm" ? "4px 10px" : "7px 14px", fontSize: size === "sm" ? 12 : 13 },
  };
  return (
    <button onClick={onClick} disabled={disabled || isLoading} style={{ ...base, ...variants[variant], ...style }}>
      {isLoading && <Spinner size={14} color={variant === "primary" ? "#fff" : "#4B5563"} />}
      {children}
    </button>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>}
      <input
        style={{
          padding: "9px 12px", border: `1px solid ${error ? "#FCA5A5" : "#D1D5DB"}`,
          borderRadius: 8, fontSize: 13, color: "#111827", fontFamily: "'DM Sans',sans-serif",
          outline: "none", transition: "border-color 0.15s",
          background: "#fff",
        }}
        {...props}
      />
      {error && <span style={{ fontSize: 11, color: "#DC2626" }}>{error}</span>}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────
export function Select({ label, options, ...props }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>}
      <select
        style={{
          padding: "9px 12px", border: "1px solid #D1D5DB", borderRadius: 8,
          fontSize: 13, color: "#111827", fontFamily: "'DM Sans',sans-serif",
          background: "#fff", cursor: "pointer",
        }}
        {...props}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────
export function Table({ headers, children, style = {} }) {
  return (
    <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #E5E7EB", ...style }}>
      <style>{`
        .cp-sticky tbody tr { background: #fff; }
        .cp-sticky th:first-child, .cp-sticky td:first-child {
          position: sticky; left: 0; z-index: 2; background: inherit;
          box-shadow: 2px 0 4px -2px rgba(0,0,0,0.15);
          max-width: 300px; min-width: 220px;
          white-space: normal !important; word-break: break-word;
        }
        .cp-sticky thead th:first-child { z-index: 3; background: #F9FAFB; }
      `}</style>
      <table className="cp-sticky" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: "11px 14px", textAlign: i > 0 ? "right" : "left",
                color: "#6B7280", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap",
                fontFamily: "'DM Sans',sans-serif",
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function TR({ children, i = 0 }) {
  return (
    <tr style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA", borderBottom: "1px solid #F3F4F6" }}>
      {children}
    </tr>
  );
}

export function TD({ children, right, bold, color, style = {} }) {
  return (
    <td style={{
      padding: "9px 14px", textAlign: right ? "right" : "left",
      fontWeight: bold ? 700 : 400, color: color || "#374151",
      whiteSpace: "nowrap", ...style,
    }}>
      {children}
    </td>
  );
}

// ─── Section title ────────────────────────────────────────────────────────────
export function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0, fontFamily: "'DM Sans',sans-serif" }}>
        {children}
      </h2>
      {sub && <p style={{ color: "#6B7280", fontSize: 13, margin: "4px 0 0" }}>{sub}</p>}
    </div>
  );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
export function KPICard({ label, value, sub, accent = "#2563EB", loading: isLoading }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
      padding: "16px 20px", borderTop: `3px solid ${accent}`,
    }}>
      <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
        {label}
      </div>
      {isLoading
        ? <Skeleton height={28} width="60%" />
        : <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", fontFamily: "'DM Sans',sans-serif" }}>{value}</div>
      }
      {sub && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── AI insight box ───────────────────────────────────────────────────────────
export function AIBox({ text, loading: isLoading, onGenerate, label = "Generate AI insight" }) {
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>AI analysis</div>
        </div>
        <Btn onClick={onGenerate} loading={isLoading} size="sm">
          ✦ {label}
        </Btn>
      </div>
      {isLoading
        ? <div style={{ display: "flex", flexDirection: "column", gap: 8 }}><Skeleton height={16} /><Skeleton height={16} width="80%" /><Skeleton height={16} width="60%" /></div>
        : text
          ? <div style={{ background: "#F8FAFC", borderRadius: 8, padding: 14, fontSize: 14, lineHeight: 1.7, color: "#374151", borderLeft: "3px solid #2563EB", whiteSpace: "pre-wrap" }}>{text}</div>
          : <div style={{ textAlign: "center", padding: "24px 0", color: "#9CA3AF", fontSize: 13 }}>Click the button above to generate AI-powered analysis</div>
      }
    </Card>
  );
}

// ─── Global CSS injected once ─────────────────────────────────────────────────
const globalStyle = document.createElement("style");
globalStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'DM Sans', sans-serif; }
  input:focus, select:focus, textarea:focus { outline: none; border-color: #2563EB !important; box-shadow: 0 0 0 3px #DBEAFE; }
`;
if (!document.getElementById("oci-global-style")) {
  globalStyle.id = "oci-global-style";
  document.head.appendChild(globalStyle);
}
